import { Box, Stack, VStack } from "@zenchef/styled-system/jsx";
import Container from "./Container";
import { FooterExposedProps } from "./ContainerFooter";
import { cx } from "@zenchef/styled-system/css";
import {
  aromaSection,
  AromaSectionVariantProps,
} from "@zenchef/styled-system/recipes";
import Title, { TitleExposedProps } from "./Title";
import { PropsWithChildren } from "react";
import Alert, { AlertProps } from "../Alert/Alert";

export interface SectionListProps {
  items: (SectionProps & { hidden?: boolean })[];
  /**
   * Custom classname for the root of the section list.
   */
  className?: string;
  /**
   * Custom classname for the items delimiter.
   */
  delimiterClassName?: string;
}

export interface SectionProps
  extends PropsWithChildren,
    Omit<AromaSectionVariantProps, "withFooter"> {
  title: TitleExposedProps;
  footer?: FooterExposedProps;
  /**
   * If true, the title will be rendered as part of the content,
   * inside of the container.
   */
  titleAsContent?: boolean;
  /**
   * Custom classname for the root of the section.
   */
  className?: string;
  /**
   * Custom classname for the title of the section.
   */
  titleClassName?: string;
  /**
   * Custom classname for the container.
   */
  containerClassName?: string;
  /**
   * Custom classname for the content of the container.
   */
  contentClassName?: string;
  /**
   * Custom classname for the footer of the section.
   */
  footerClassName?: string;
  /**
   * Custom alert near to the title the section.
   */
  alertProps?: AlertProps;
}

function SectionListItemDelimiter({ className }: { className?: string }) {
  return (
    <Box
      className={className}
      width="100%"
      margin="auto"
      maxWidth={{
        base: "1120px",
        mdDown: "912px",
      }}
      borderBottomWidth="1px"
      borderColor="border.neutral.bold"
    />
  );
}

function SectionList({
  items,
  className,
  delimiterClassName,
}: SectionListProps) {
  return (
    <VStack
      className={className}
      alignItems="initial"
      gap="gap.6"
      margin="auto"
      width="100%"
      maxWidth={{
        base: "1120px",
        mdDown: "912px",
      }}
    >
      {items
        .filter((item) => !item.hidden)
        .reduce<JSX.Element[]>((accumulator, item, index) => {
          if (accumulator.length === 0) {
            return [...accumulator, <Section key={index} {...item} />];
          }

          return [
            ...accumulator,
            <SectionListItemDelimiter
              key={`${index}-delimiter`}
              className={delimiterClassName}
            />,
            <Section key={index} {...item} />,
          ];
        }, [])}
    </VStack>
  );
}

function Section({
  title,
  footer,
  titleAsContent = false,
  className,
  titleClassName,
  containerClassName,
  contentClassName,
  footerClassName,
  alertProps,
  children,
}: SectionProps) {
  const classes = aromaSection({
    withFooter: !!footer,
    titleAsContent,
  });

  return (
    <Box className={cx(classes.root, className)}>
      {!titleAsContent ? (
        alertProps ? (
          <Stack gap="gap.2" flexDir={{ base: "row", smDown: "column" }}>
            <Title {...title} className={cx(classes.title, titleClassName)} />
            <Alert {...alertProps} />
          </Stack>
        ) : (
          <Title {...title} className={cx(classes.title, titleClassName)} />
        )
      ) : null}
      <Container
        title={titleAsContent ? title : null}
        alertProps={titleAsContent ? alertProps : undefined}
        className={containerClassName}
        contentClassName={contentClassName}
        titleClassName={titleClassName}
        footerClassName={footerClassName}
        footer={footer}
      >
        {children}
      </Container>
    </Box>
  );
}

export default Object.assign(Section, {
  List: SectionList,
});
